/**
 * Our portfolio — SPEC_5 §4.4: the DataPack as the organizer wrote it, never our own arithmetic on
 * it. Four figures, yesterday's orders only when something went wrong with them, and the holdings
 * with each one's weight (2026-10-22 redesign: the NAV chart, lots, closes and the weight bar went).
 *
 * What the DataPack doesn't carry — a holding's name, its caps, its streak over the official cap and
 * the void reasons in words — the server adds beside it (`/api/portfolio.holdings`,
 * `.prev_day_orders`), so the console holds no copy of the 150 or the rules.
 *
 * Colours: a price change is 漲紅跌綠 (`--up`/`--down`), never `--danger`. A weight is warn above our
 * buffered cap and danger above the official one, with a word beside it.
 */

import type { Holding, Portfolio as PortfolioPayload } from "../../api/types/competition";
import { useT } from "../../i18n/useT";
import { moved, nt, pct, share } from "../../lib/fmt";
import { Figure, Figures } from "../ui/Figure";

/** Every team starts with this (the competition's rules), and the NAV change is measured from it. */
const START_NAV = 1_000_000_000;

export function Portfolio({ portfolio }: { portfolio: PortfolioPayload }) {
  const { t } = useT();
  const dp = portfolio.datapack;
  if (!dp) return <p className="empty">{t("pf.none")}</p>;
  const account = dp.account;
  const violations = dp.compliance.violations;
  // Yesterday's orders are shown only when something went wrong with them: a day that traded as planned
  // (or isn't decided yet) needs no line saying so.
  const orders = portfolio.prev_day_orders;
  const rows = portfolio.holdings
    .filter((h) => h.shares > 0 || h.receivable_shares > 0)
    .sort((a, b) => (b.tradable_weight ?? -1) - (a.tradable_weight ?? -1));

  return (
    <>
      <Figures>
        <Figure
          label={t("pf.nav")}
          info={t("info.nav")}
          value={<span data-figure="nav">{account ? nt(account.nav_tradable) : "—"}</span>}
          says={
            account ? (
              <span className={moved(account.nav_tradable - START_NAV)}>
                {t("pf.sinceStart", { pct: pct(account.nav_tradable / START_NAV - 1, 2) })}
              </span>
            ) : null
          }
        />
        <Figure
          label={t("pf.cash")}
          info={t("info.cash")}
          value={<span data-figure="cash">{account ? share(account.cash_ratio_tradable) : "—"}</span>}
        />
        <Figure
          label={t("pf.holdings")}
          info={t("info.holdings")}
          value={<span data-figure="holdings">{account ? account.holdings_count : "—"}</span>}
        />
        <Figure
          label={t("pf.warnings")}
          info={t("info.warnings")}
          value={
            <span data-figure="violations" className={violations >= 1 ? "danger" : undefined}>
              {violations} / 3
            </span>
          }
        />
      </Figures>

      {orders && (orders.outcome === "voided" || orders.outcome === "none" || orders.void_reasons.length || orders.excluded_codes.length) ? (
        <div className="orders" data-outcome={orders.outcome}>
          <p>
            <span className="sub">{t("pf.yesterday")}</span> <b>{orders.outcome}</b>
            {" · "}
            {orders.says}
          </p>
          {orders.void_reasons.map((v) => (
            <p key={v.code} className="orders__void">
              <span className="num">{v.code}</span> · {v.says}
            </p>
          ))}
          {orders.excluded_codes.length ? (
            <p className="sub">{t("pf.excluded", { codes: orders.excluded_codes.join(", ") })}</p>
          ) : null}
        </div>
      ) : null}

      <div className="holdings" tabIndex={0} aria-label={t("pf.holdingsTable")}>
        <table className="book">
          <thead>
            <tr>
              <th scope="col">{t("pf.code")}</th>
              <th scope="col">{t("pf.name")}</th>
              <th scope="col" className="ra">
                {t("pf.weight")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <Row key={h.code} h={h} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Row({ h }: { h: Holding }) {
  const { t } = useT();
  const w = h.tradable_weight;
  const level = w === null ? "none" : w > h.cap ? "over" : w > h.buffered_cap ? "near" : "ok";
  return (
    <tr data-code={h.code}>
      <td className="sym num">{h.code}</td>
      <td lang="zh-Hant">{h.name ?? <span className="muted">{t("pf.outside")}</span>}</td>
      <td className="ra">
        <span className="weight num" data-level={level}>
          {share(w)}
        </span>
        {level === "over" ? <span className="weight__word" data-level={level}>{t("pf.overCap", { cap: share(h.cap, 0) })}</span> : null}
        {level === "near" ? (
          <span className="weight__word" data-level={level}>{t("pf.overBuffer", { cap: share(h.buffered_cap, 0) })}</span>
        ) : null}
        {h.days_over_cap > 0 ? (
          <span className="sub overdays" data-days-over-cap={h.days_over_cap}>
            {t(h.days_over_cap === 1 ? "pf.dayOver" : "pf.daysOver", { n: h.days_over_cap })}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
